import React from 'react';
import { InfoOutlined, BarChartOutlined } from '@mui/icons-material';
import { Tooltip, TitleCard } from '../../../../../components';
import { getTooltipContent, type AllMetricType } from '../utils';

export type BaseCardData = {
  id: string;
  title: string;
  type: AllMetricType;
};

type BaseCardProps<T extends BaseCardData = BaseCardData> = {
  item: T;
  onClick?: (item: T) => void;
  children: React.ReactNode;
  className?: string;
  iconSize?: 'default' | 'small';
  headerClassName?: string;
  contentClassName?: string;
  showChartButton?: boolean;
  showCardTooltip?: boolean;
  onChartClick?: (item: T) => void;
  hideTitle?: boolean;
  customHeaderActions?: React.ReactNode;
};

export function BaseCard<T extends BaseCardData = BaseCardData>({
  item,
  onClick,
  children,
  className = 'w-full flex-1 flex flex-col bg-white rounded-card cursor-pointer p-3 xl:p-4 shadow-sm hover:shadow-md transition-all duration-200 focus:outline-none focus-visible:outline-none',
  iconSize = 'default',
  headerClassName = 'flex justify-between items-start mb-1 xl:mb-2',
  contentClassName = 'flex-1',
  showChartButton = false,
  onChartClick,
  showCardTooltip = true,
  hideTitle = false,
  customHeaderActions,
}: BaseCardProps<T>) {
  return (
    <div className={className} onClick={() => onClick?.(item)}>
      {!hideTitle && (
        <div className={headerClassName}>
          <TitleCard
            title={item.title}
            type={item.type}
            iconSize={iconSize}
            className="flex gap-2 sm:gap-1 xl:gap-sm items-center"
          />
          <div className="flex items-center gap-2">
            {showCardTooltip && (
              <Tooltip
                content={getTooltipContent(item.type)}
                position="top"
                iconClassName="text-secondary hover:text-main transition-colors"
                popupClassName="max-w-xs"
                customIcon={<InfoOutlined style={{ fontSize: 16 }} />}
              />
            )}
          </div>
        </div>
      )}
      {customHeaderActions}

      <div className={`${contentClassName} relative`}>
        {children}

        {showChartButton && (
          <button
            onClick={e => {
              e.stopPropagation();
              onChartClick?.(item);
            }}
            className="absolute bottom-2 right-2 p-1.5 rounded-sm bg-white shadow-sm hover:shadow-md transition-all duration-200 border border-primaryIcon hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
            title="View detailed chart"
          >
            <BarChartOutlined
              style={{
                fontSize: 16,
                color: 'var(--primary-icon)',
              }}
            />
          </button>
        )}
      </div>
    </div>
  );
}
